import { tKey, type I18nKey } from "@/core/i18n";
import { useTranslation } from "react-i18next";
import { LabelEntry } from "./types";

type AreaStat = Exclude<keyof NonNullable<LabelEntry["area"]>, "outlierCount" | "outliers">;

/** Tooltip rows, top to bottom in the order the box plot draws them. */
const ROWS: [I18nKey<"analytics">, AreaStat][] = [
  [tKey("analytics:boxPlot.max"), "max"],
  [tKey("analytics:boxPlot.whiskerHigh"), "whiskerHigh"],
  [tKey("analytics:boxPlot.q3"), "q3"],
  [tKey("analytics:boxPlot.median"), "median"],
  [tKey("analytics:boxPlot.q1"), "q1"],
  [tKey("analytics:boxPlot.whiskerLow"), "whiskerLow"],
  [tKey("analytics:boxPlot.min"), "min"],
];

interface BoxPlotTooltipProps {
  active?: boolean;
  payload?: {
    payload: { name: string; color: string; area: LabelEntry["area"] };
  }[];
}

export const BoxPlotTooltip = ({ active, payload }: BoxPlotTooltipProps) => {
  const { t } = useTranslation(["analytics", "common"]);
  if (!active || !payload?.length) return null;
  const { name, color, area } = payload[0].payload;
  if (!area) return null;

  const fmt = (v: number) => `${(v * 100).toFixed(2)}%`;

  return (
    <div className="rounded-lg border border-black/10 bg-background p-3 text-xs shadow-md">
      <div className="mb-2 flex items-center gap-2 font-semibold">
        <span className="inline-block size-2.5 rounded-sm" style={{ background: color }} />
        {name}
      </div>
      <table className="w-full border-separate border-spacing-x-3">
        <tbody>
          {ROWS.map(([label, stat]) => (
            <tr key={stat}>
              <td className="text-muted-foreground">{t(label)}</td>
              <td className="text-right font-mono">{fmt(area[stat])}</td>
            </tr>
          ))}
          {area.outlierCount > 0 && (
            <tr>
              <td className="text-muted-foreground">{t("boxPlot.outliers")}</td>
              <td className="text-right font-mono">{area.outlierCount}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
